// docs/140 — dungeon traps on the client: the book (every entry's words, trigger, script and look,
// from wasm `snare_book`), the call-out a trap's spring makes, the words for its curio and for a
// found plate or wire, and which squares a running trap holds (blades, cages, nets). Every word is
// the sim's or the storyteller's; this file only looks them up and lays them out. Pure functions.
import { wasmModule } from '../sim/wasm';
import type { ActKind, CrawlView, CurioName, Effect, FixtureView, InteractVerb, ResolutionEntry, SnareTrigger, SnareView, Tile } from '../sim/types';

/** One beat of an entry's script: when it begins, its kind, and the act with its numbers (serde: `{ Flood: { liquid, per, share, hold } }`). */
export interface SnareBeat { at: number; kind: ActKind; act: Record<string, Record<string, unknown> | undefined> | string }
/** `fk-sim-wasm::snare_book` — one entry of the book (docs/140 DT1). */
export interface SnareEntry {
  id: number; key: string; name: string; guise: string; line: string; cry: string;
  trigger: unknown; host: 'None' | 'Curio' | 'Chest' | 'Door'; curio?: CurioName | null;
  stage: string; dest: string; brood?: { folk: string; n: number; name: string } | null;
  beats: SnareBeat[]; stop?: CurioName | null; land: string; themes: string[]; danger: number; sound: string;
}

let book: SnareEntry[] | undefined;

/** The book, in id order (empty until wasm is up; asked again next time). */
export function snareBook(): readonly SnareEntry[] {
  if (book) return book;
  try { book = wasmModule().snare_book() as SnareEntry[]; } catch { return []; }
  return book;
}

/** Book entry `def` (a snare's `def`, a `SnareSprung.def`). */
export function snareDef(def: number): SnareEntry | undefined { return snareBook()[def]; }

/** For tests and the gallery: set the book by hand. */
export function setSnareBook(b: SnareEntry[] | undefined): void { book = b; }

/** The trigger in serde shape (`"Step"`, `{ Work: "Pull" }`, `{ Crowd: 3 }`) → `SnareTrigger`. */
export function triggerOf(t: unknown): SnareTrigger {
  if (typeof t === 'string') return { kind: t as SnareTrigger['kind'] };
  if (t && typeof t === 'object') {
    const [k, v] = Object.entries(t as Record<string, unknown>)[0] ?? ['Step', undefined];
    if (k === 'Work') return { kind: 'Work', verb: v as InteractVerb };
    return { kind: k as SnareTrigger['kind'], ...(typeof v === 'number' ? { n: v } : {}) };
  }
  return { kind: 'Step' };
}


/** What springs it, in words: "a pressure plate", "a lever pulled", "a foe's card (reach 4)". */
export function triggerWords(t: SnareTrigger, e?: Pick<SnareEntry, 'host' | 'curio'>): string {
  switch (t.kind) {
    case 'Step': return 'a pressure plate';
    case 'Wire': return 'a tripwire';
    case 'Work': {
      if (e?.host === 'Chest') return 'opening a chest';
      if (e?.host === 'Door') return 'opening a door';
      return e?.curio ? curioLabel({ name: '', curio: e.curio }).toLowerCase() : 'working something';
    }
    case 'Enter': return 'walking in';
    case 'Crowd': return `${t.n ?? 2} heroes in the room`;
    case 'Linger': return `lingering ${t.n ?? 2} turns`;
    case 'Clock': return `${t.n ?? 2} turns after entering`;
    case 'Card': return `a foe's card (reach ${t.n ?? 4})`;
    case 'Slay': return 'its keeper slain';
    case 'Loot': return 'a chest opened';
    case 'Gaze': return `meeting its gaze (${t.n ?? 3} squares)`;
  }
}

/** The trigger kinds the gallery filters by, in a sensible order. */
export const TRIGGER_KINDS: readonly SnareTrigger['kind'][] = ['Step', 'Wire', 'Work', 'Enter', 'Crowd', 'Linger', 'Clock', 'Gaze', 'Loot', 'Card', 'Slay'];

/** Danger 0–3 in a word (docs/140 DT1). */
export const DANGER_WORDS = ['a prank', 'a scare', 'a real hurt', 'a deadly race'] as const;

// ---- curios ---------------------------------------------------------------------------------

/** `Curio::name`: what a curio goes by on the board. */
const CURIO_NAMES: Record<CurioName, string> = {
  Lever: 'a lone lever', SkullLever: 'a lever with a skull for a handle', Rope: 'a rope hanging from the dark', Idol: 'a golden idol on a pedestal',
  Throne: 'an empty throne', Fountain: 'a little fountain', Mirror: 'a tall mirror', Gong: 'a great gong', Bell: 'a bronze bell',
  Tome: 'a book left open on a lectern', GemStatue: 'a statue with gems for eyes', Wheel: 'a stone wheel', Coffin: 'a coffin', Brazier: 'a cold brazier',
  CrystalBall: 'a crystal ball', Painting: 'a portrait', MushroomRing: 'a ring of mushrooms', Cask: 'a giant cask with a tap', Crown: 'a crown on a cushion',
  GoldHeap: 'a heap of gold coins', Hourglass: 'a great hourglass', SwordInStone: 'a sword in a stone', Urn: 'a sealed urn', Scarecrow: 'a scarecrow',
  FrozenFace: 'a face frozen in the ice', DragonHead: "a carved dragon's head", Horn: 'a great bronze horn', Hive: 'a papery nest', Candle: 'a guttering candle',
  Bait: 'a ham on a hook', TwinStatues: 'two statues facing each other', RuneCircle: 'a circle of runes', Cauldron: 'a bubbling cauldron', Well: 'a wishing well',
  Mat: 'a mat that says WELCOME', Vent: 'a grated vent', Cogwheel: 'a cogwheel', Plank: 'a loose plank', Pool: 'a still, dark pool',
};
export function curioName(c: CurioName): string { return CURIO_NAMES[c] ?? 'a curious thing'; }

/** `Curio::verb`: the one verb that works it (anything else only looks at it). */
export function curioVerb(c: CurioName): InteractVerb {
  switch (c) {
    case 'Lever': case 'SkullLever': case 'Rope': case 'SwordInStone': return 'Pull';
    case 'Idol': case 'Crown': case 'GoldHeap': case 'Bait': return 'Take';
    case 'Wheel': case 'Cask': case 'Hourglass': case 'Cogwheel': return 'Turn';
    case 'Tome': case 'Scarecrow': case 'FrozenFace': case 'Painting': case 'CrystalBall': case 'Pool': return 'Examine';
    case 'Urn': case 'Vent': case 'Plank': return 'Push';
    case 'Coffin': return 'Open';
    default: return 'Touch';
  }
}

/** `Curio::solid`: it stands on its square like a wall, or lies on the floor to be stepped on. */
export function curioSolid(c: CurioName): boolean {
  return !(c === 'MushroomRing' || c === 'GoldHeap' || c === 'Mat' || c === 'RuneCircle' || c === 'Pool' || c === 'Plank');
}

/** The verb, said for this curio: "Sit on", "Look into", "Ring". */
const CURIO_SAY: Partial<Record<CurioName, string>> = {
  Throne: 'Sit on', Pool: 'Look into', Well: 'Toss a coin into', Mirror: 'Touch', Gong: 'Strike', Bell: 'Ring', Tome: 'Read',
  CrystalBall: 'Gaze into', Painting: 'Study', Scarecrow: 'Look over', FrozenFace: 'Peer at', Fountain: 'Drink from', Horn: 'Blow',
  Brazier: 'Light', Candle: 'Pinch out', Cauldron: 'Stir', Hive: 'Poke', MushroomRing: 'Step into', RuneCircle: 'Step into', Mat: 'Wipe your feet on',
  DragonHead: "Touch", TwinStatues: 'Touch', Coffin: 'Open', Urn: 'Push over', Vent: 'Kick', Plank: 'Step on', Cask: 'Turn the tap of',
  Hourglass: 'Turn', Wheel: 'Turn', Cogwheel: 'Turn', SwordInStone: 'Pull', Rope: 'Pull', Lever: 'Pull', SkullLever: 'Pull',
  Idol: 'Take', Crown: 'Take', GoldHeap: 'Take', Bait: 'Take',
};

/** "the lone lever" from "a lone lever" (the fixture's own name, else the curio's). */
export function curioNoun(f: Pick<FixtureView, 'name' | 'curio'>): string {
  const n = (f.name || (f.curio ? curioName(f.curio) : 'it')).split(' — ')[0]!.trim();
  return n.replace(/^(a|an) /i, 'the ');
}

/** What a click does to a curio: "Pull the lone lever", "Sit on the empty throne", "Take the golden idol on a pedestal". */
export function curioLabel(f: Pick<FixtureView, 'name' | 'curio'>): string {
  const say = (f.curio && CURIO_SAY[f.curio]) || 'Touch';
  return `${say} ${curioNoun(f)}`;
}
/** The chip's short word ("Pull", "Sit on"). */
export function curioShort(f: Pick<FixtureView, 'curio'>): string { return (f.curio && CURIO_SAY[f.curio]) || 'Touch'; }

/** The hover line for a curio: its guise, and that it might do something. */
export function curioLine(f: Pick<FixtureView, 'name' | 'curio' | 'on'>): string {
  const n = f.name || (f.curio ? curioName(f.curio) : 'something');
  const name = n.charAt(0).toUpperCase() + n.slice(1);
  if (f.on) return `${name} — it has done its worst`;
  return `${name} — ${(f.curio && CURIO_SAY[f.curio]?.toLowerCase()) || 'touch'} it? Who knows what it does`;
}

// ---- the crawl's traps in the party's view ---------------------------------------------------

/** The trap a log entry is about, by its truth id (the party's view keeps the truth's ids). */
export function snareOf(crawl: CrawlView | undefined, id: number): SnareView | undefined { return crawl?.snares.find((s) => s.id === id); }

/** A found, armed plate or wire under `t` (a Search found it): what a click on the square is about. */
export function knownSnareAt(crawl: CrawlView | undefined, t: Tile): SnareView | undefined {
  return crawl?.snares.find((s) => s.state === 'Armed' && s.known && (s.trigger.kind === 'Step' || s.trigger.kind === 'Wire') && underfoot(s).some((u) => u.x === t.x && u.y === t.y));
}

/** `Snare::underfoot`: the squares that spring it (a plate's anchor, a wire's whole run). */
export function underfoot(s: Pick<SnareView, 'trigger' | 'anchor' | 'trip'>): Tile[] {
  if (s.trigger.kind === 'Step') return [s.anchor];
  if (s.trigger.kind === 'Wire') return s.trip.some((t) => t.x === s.anchor.x && t.y === s.anchor.y) ? s.trip : [...s.trip, s.anchor];
  return [];
}

/** What a found plate or wire goes by: its guise ("a flagstone that rings hollow"). */
export function snareGuise(s: Pick<SnareView, 'def' | 'trigger'>): string {
  const d = snareDef(s.def);
  return d?.guise || (s.trigger.kind === 'Wire' ? 'a tripwire' : 'a pressure plate');
}

/** The hover line for a found plate or wire. */
export function snareLine(s: SnareView): string {
  const g = snareGuise(s);
  const what = s.trigger.kind === 'Wire' ? 'a tripwire' : 'a pressure plate';
  return `${g.charAt(0).toUpperCase()}${g.slice(1)} — ${what}, found; stepping on it springs a trap. A rogue can disarm it`;
}

// ---- the spring's call-out ------------------------------------------------------------------

/** The big card a trap's spring raises: TRAP! (or A FOE'S TRICK!), its name, its words, its guise unmasked. */
export interface SnareCallout { eyebrow: string; head: string; line: string; reveal?: string; cry?: string; by?: number; victim?: number; tile: Tile; danger: number }

export function snareCallout(e: Extract<Effect, { kind: 'SnareSprung' }>, crawl: CrawlView | undefined, name: (unit: number) => string | undefined): SnareCallout {
  const d = snareDef(e.def);
  const s = snareOf(crawl, e.snare);
  const card = e.by !== undefined;
  const line = (s?.line || d?.line || '').trim();
  const out: SnareCallout = { eyebrow: card ? "A FOE'S TRICK!" : 'TRAP!', head: d?.name ?? 'A trap!', line, tile: e.tile, danger: d?.danger ?? 1 };
  // the guise unmasked: "a flagstone that rings hollow → The Oubliette Trapdoor"
  if (!card && d?.guise) out.reveal = d.guise;
  if (card) {
    out.by = e.by;
    if (d?.cry) out.cry = d.cry;
    const foe = name(e.by!);
    if (foe) out.eyebrow = `${foe.toUpperCase()} PLAYS A TRICK!`;
  }
  if (e.victim !== undefined) out.victim = e.victim;
  return out;
}

/** True for the effects dungeon traps add to the log. */
export function isSnareEffect(e: Effect): e is Extract<Effect, { kind: 'SnareSprung' | 'SnareActed' | 'SnareEnded' | 'SnareFound' | 'SnareDisarmed' }> {
  return e.kind === 'SnareSprung' || e.kind === 'SnareActed' || e.kind === 'SnareEnded' || e.kind === 'SnareFound' || e.kind === 'SnareDisarmed';
}

/** A trap's own entry, or one it caused (its blows, shoves, ground, the creatures it loosed). */
export function isSnareEntry(e: ResolutionEntry): boolean { return isSnareEffect(e.effect) || e.cause.kind === 'Snare'; }

// ---- reading an entry's script --------------------------------------------------------------

/** The numbers of the first beat of kind `kind` in entry `def` (`{ liquid: 'Water', per: 3, … }`). */
export function actOf(def: number, kind: ActKind): Record<string, unknown> | undefined {
  const d = snareDef(def);
  const b = d?.beats.find((x) => x.kind === kind);
  if (!b) return undefined;
  if (typeof b.act === 'string') return {};
  return (b.act[kind] as Record<string, unknown> | undefined) ?? {};
}

/** What rises in a flood (`Liquid` name), else Water. */
export function floodLiquid(def: number): string { return String(actOf(def, 'Flood')?.['liquid'] ?? 'Water'); }
/** What rolls (`Roller` name), else Boulder. */
export function rollerOf(def: number): string { return String(actOf(def, 'Roll')?.['thing'] ?? 'Boulder'); }
/** A jet's element (`Element` name), else Fire. */
export function jetElement(def: number): string { return String(actOf(def, 'Jet')?.['element'] ?? 'Fire'); }
/** A blow's element, if it has one. */
export function hurtElement(def: number): string | undefined { const e = actOf(def, 'Hurt')?.['element']; return e ? String(e) : undefined; }

/** How a liquid is tinted as it rises (water, lava, sand, ooze, ale, tar, snow, freezing water). */
export const LIQUID_TINT: Record<string, { fill: number; edge: number; glow?: boolean; name: string }> = {
  Water: { fill: 0x2f6fb5, edge: 0x9fd4ff, name: 'water' },
  Icewater: { fill: 0x6fb6d8, edge: 0xe4f7ff, name: 'freezing water' },
  Lava: { fill: 0xd2421a, edge: 0xffc14d, glow: true, name: 'lava' },
  Sand: { fill: 0xc9a35e, edge: 0xf0d79a, name: 'sand' },
  Ooze: { fill: 0x5e9e3a, edge: 0xb8f07a, name: 'ooze' },
  Ale: { fill: 0xc58a2a, edge: 0xfff0c0, name: 'ale' },
  Tar: { fill: 0x1e1a18, edge: 0x6b5f55, name: 'tar' },
  Snow: { fill: 0xe8eef5, edge: 0xffffff, name: 'snow' },
};

/** An element's colours (fire, frost, radiance, lightning, acid, poison, force…). */
export function elementTint(el: string | undefined): { core: number; edge: number } {
  switch ((el ?? '').toLowerCase()) {
    case 'fire': return { core: 0xff7a1a, edge: 0xffe08a };
    case 'frost': case 'cold': case 'ice': return { core: 0x8fd8ff, edge: 0xf0fbff };
    case 'radiant': case 'radiance': case 'holy': return { core: 0xffe27a, edge: 0xfffbe0 };
    case 'lightning': case 'shock': case 'storm': return { core: 0x9fb8ff, edge: 0xffffff };
    case 'acid': return { core: 0x9be02c, edge: 0xe9ff9a };
    case 'poison': return { core: 0x7ac943, edge: 0xd8ff9a };
    case 'necrotic': case 'shadow': return { core: 0x7a4fb5, edge: 0xd3b8ff };
    case 'thunder': case 'force': return { core: 0xc9c2ff, edge: 0xffffff };
    default: return { core: 0xd8d0c0, edge: 0xffffff };
  }
}

/** Atlas frame names the art helper draws (fallbacks are drawn when the atlas lacks them). */
export const curioFrame = (c: CurioName): string => `snare-curio-${c.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()}`;
export const rollerFrame = (r: string): string => `snare-roller-${r.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()}`;
export const SNARE_FX = { plate: 'snare-fx-plate', wire: 'snare-fx-wire', blade: 'snare-fx-blade', cage: 'snare-fx-cage', net: 'snare-fx-net', spikes: 'snare-fx-spikes', dart: 'snare-fx-dart', portcullis: 'snare-fx-portcullis', slab: 'snare-fx-slab' } as const;

/** A held victim's look while a trap runs: a cage or a net, by the beats still going. */
export function heldBy(s: SnareView): Array<{ act: 'Cage' | 'Hoist'; units: number[] }> {
  const d = snareDef(s.def);
  if (!d || s.state !== 'Running') return [];
  const out: Array<{ act: 'Cage' | 'Hoist'; units: number[] }> = [];
  d.beats.forEach((b, i) => {
    const p = s.progress[i] ?? 0;
    if ((b.kind === 'Cage' || b.kind === 'Hoist') && p > 0 && p !== 0xffff && s.victim !== undefined) out.push({ act: b.kind, units: [s.victim] });
  });
  return out;
}

/** Whether a running trap's Swing beat is going (its blades stand on their lanes). */
export function swinging(s: SnareView): boolean {
  const d = snareDef(s.def);
  if (!d || s.state !== 'Running') return false;
  return d.beats.some((b, i) => b.kind === 'Swing' && (s.progress[i] ?? 0) > 0 && s.progress[i] !== 0xffff);
}

/** A blade's squares on a lane: three from the wall (docs/140 DT5 Swing). */
export function laneSquares(l: { tile: Tile; dir: string }, n = 3): Tile[] {
  const step: Record<string, [number, number]> = { N: [0, -1], S: [0, 1], E: [1, 0], W: [-1, 0], NE: [1, -1], NW: [-1, -1], SE: [1, 1], SW: [-1, 1] };
  const [dx, dy] = step[l.dir] ?? [0, 1];
  return Array.from({ length: n }, (_, i) => ({ x: l.tile.x + dx * i, y: l.tile.y + dy * i }));
}

/** "The Drowned Abbot wakes" for the dweller's own lair, "The bone-pickers wake" for a band (docs/140 DT13). */
export function wakeHead(name: string, single: boolean): string {
  const n = name.charAt(0).toUpperCase() + name.slice(1);
  return `${n} ${single ? 'wakes' : 'wake'}`;
}

/**
 * How trap moments sound on this page: live play hears the server's cues (the module's director
 * scores a trap's spring and acts); the `?snares=1` gallery has no server for the cackle and the
 * acts, so it plays them itself (`localSounds`) unless its frames already carry `trap.*` cues.
 */
export const snarePresentation = { localSounds: false };
